import DefaultLoader from '@app/components/various/loaders/DefaultLoader';
import {
  fetchOrderFailure,
  fetchOrderSuccess,
  getIsDeliveryWindowsLoading,
  getIsLoggedIn,
  getSharedLinksEnabled,
  resetCheckoutForm,
  resetFilters,
  resetPageState,
  setDeliveryWindows,
  setExpandedProductFilters,
  sharedSelectionRequest,
} from '@app/ducks';
import { getIsOrderEditable, getIsOrderSplit } from '@app/logic/Orders';
import { compiledPaths, paths } from '@app/paths';
import { useOrderDetails } from '@app/services/hooks/orders/useOrderDetails';
import { getQueryParams } from '@app/utils/getQueryParams';
import { isDefined } from '@app/utils/is';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useMatch, useNavigate, useParams } from 'react-router-dom';

export const SelectionLoader = ({ children }: React.WithChildren) => {
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();
  const selectionId = id as string;
  const isCurrentUrlCheckoutSuccess = useMatch(paths.CHECKOUT_SUCCESS)?.pattern.end ?? false;
  const areSharedLinksEnabled = useSelector(getSharedLinksEnabled);
  const isLoggedIn = useSelector(getIsLoggedIn);
  const [loadedSelectionId, setLoadedSelectionId] = React.useState<string | null>(null);
  const navigate = useNavigate();
  const isLoadingDeliveryWindows = useSelector(getIsDeliveryWindowsLoading);
  const {
    data: orderDetails,
    isPending: isPendingOrderDetails,
    error: orderDetailsError,
  } = useOrderDetails({ orderId: id, refetchOnMount: false });

  const isOrderReadOnly = React.useMemo(() => {
    if (!isDefined(orderDetails)) {
      return false;
    }

    const isSplitOrder = getIsOrderSplit(orderDetails);
    if (isSplitOrder) {
      return true;
    }

    const canModify = isDefined(orderDetails.order.canModify) && orderDetails.order.canModify;
    const isOrderClosed = isDefined(orderDetails.order.status) && !getIsOrderEditable(orderDetails.order.status);

    return (isOrderClosed || !canModify) && isDefined(orderDetails.order.order);
  }, [orderDetails]);

  React.useEffect(() => {
    if (!isDefined(orderDetails)) {
      return;
    }

    dispatch(setExpandedProductFilters({}));
    dispatch(resetFilters());

    const isSplitOrder = getIsOrderSplit(orderDetails);

    const orderPayload =
      isSplitOrder ?
        {
          ...orderDetails,
          originalOrderId: id,
        }
      : orderDetails;

    const mixedDeliveryWindows = isSplitOrder ? [] : orderDetails.deliveryWindows ?? [];
    dispatch(fetchOrderSuccess(orderPayload));
    dispatch(setDeliveryWindows(mixedDeliveryWindows));
    dispatch(resetCheckoutForm());
    dispatch(resetPageState());
    setLoadedSelectionId(selectionId);
  }, [dispatch, orderDetails, selectionId]);

  React.useEffect(() => {
    if (orderDetailsError && isDefined(id)) {
      const { sharedToken } = getQueryParams<{ sharedToken?: string }>(window.location.search);

      if (areSharedLinksEnabled && isDefined(sharedToken)) {
        dispatch(sharedSelectionRequest({ orderId: id, sharedToken }));
      } else {
        const redirectPath = isLoggedIn ? paths.PAGE_NOT_FOUND : paths.LOGIN;
        dispatch(fetchOrderFailure());
        navigate(redirectPath, { replace: true });
      }
    }
  }, [areSharedLinksEnabled, dispatch, id, isLoggedIn, orderDetailsError, navigate]);

  const shouldNavigateToCheckoutSuccess = !isCurrentUrlCheckoutSuccess && isOrderReadOnly;
  const shouldShowLoader = loadedSelectionId !== selectionId || isPendingOrderDetails || isLoadingDeliveryWindows;

  if (shouldShowLoader) {
    return <DefaultLoader overlay />;
  }

  if (shouldNavigateToCheckoutSuccess) {
    return <Navigate to={compiledPaths.CHECKOUT_SUCCESS({ id: selectionId })} replace />;
  }

  return children;
};
